import { useEffect, useState, type ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import * as Dialog from '@radix-ui/react-dialog';
import { AnimatePresence, motion, useDragControls, type PanInfo } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useVisualViewportHeight } from '@shared/hooks/useVisualViewportHeight';
import { cn } from '@shared/lib/cn';

/**
 * A tall list sheet as the mocks draw it (History, All contacts): paper, 24px top corners, the title
 * beside a round Close, a scrolling list and an optional footer pinned under it. Like the app's bottom
 * sheet it slides up, closes on the scrim, Escape or a downward swipe on its header, and sits above
 * the on-screen keyboard.
 */
export function ListSheet({
  open,
  onOpenChange,
  title,
  footer,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Buttons pinned under the list, such as Done. */
  footer?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  const dragControls = useDragControls();
  const keyboardInset = useKeyboardInset(open);

  const dismissOnSwipe = (_event: unknown, info: PanInfo) => {
    if (info.offset.y > 120 || info.velocity.y > 600) onOpenChange(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open ? (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                className="fixed inset-0 z-50 bg-[rgba(16,24,20,0.45)]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
              />
            </Dialog.Overlay>
            <Dialog.Content asChild aria-describedby={undefined} onOpenAutoFocus={(event) => event.preventDefault()}>
              <motion.div
                className="fixed inset-x-0 bottom-0 z-50 flex h-[78dvh] flex-col rounded-t-[24px] bg-paper pb-safe shadow-[0_-8px_32px_-8px_rgba(0,0,0,0.3)]"
                // 1. While the keyboard is up, sit on top of it and fill the height it leaves.
                style={keyboardInset ? { bottom: keyboardInset, height: `calc(92dvh - ${keyboardInset}px)` } : undefined}
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ duration: 0.26, ease: [0.32, 0.72, 0, 1] }}
                drag="y"
                dragControls={dragControls}
                dragListener={false}
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={{ top: 0, bottom: 0.55 }}
                onDragEnd={dismissOnSwipe}
              >
                {/* 2. The title and a down arrow that slides the sheet away; the header is the swipe handle. */}
                <header
                  onPointerDown={(event) => dragControls.start(event)}
                  className="flex shrink-0 touch-none items-center justify-between px-4 pb-2.5 pt-[18px]"
                >
                  <Dialog.Title className="m-0 text-lg font-bold leading-[normal] text-ink">{title}</Dialog.Title>
                  <Dialog.Close
                    aria-label="Close"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-hairline bg-card text-ink"
                  >
                    <ChevronDown className="h-5 w-5" strokeWidth={2} />
                  </Dialog.Close>
                </header>

                {/* 3. The list, scrolling beneath the header, and any footer pinned under it. */}
                <div className={cn('min-h-0 flex-1 overflow-y-auto px-4', footer ? 'pb-3' : 'pb-6')}>{children}</div>
                {footer ? <div className="flex shrink-0 flex-col gap-2 px-4 pb-4 pt-3 hairline-t">{footer}</div> : null}
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        ) : null}
      </AnimatePresence>
    </Dialog.Root>
  );
}

/**
 * How far the keyboard covers the screen bottom, as the app's bottom sheet measures it: the shrunk
 * visual viewport in a browser, the keyboard plugin on iOS (whose WebView does not resize).
 */
function useKeyboardInset(open: boolean): number {
  const viewportHeight = useVisualViewportHeight();
  const [iosInset, setIosInset] = useState(0);

  useEffect(() => {
    if (!open || Capacitor.getPlatform() !== 'ios') return;
    const shown = Keyboard.addListener('keyboardWillShow', (info) => setIosInset(info.keyboardHeight));
    const hidden = Keyboard.addListener('keyboardWillHide', () => setIosInset(0));
    return () => {
      void shown.then((listener) => listener.remove());
      void hidden.then((listener) => listener.remove());
      setIosInset(0);
    };
  }, [open]);

  return Math.max(viewportHeight ? window.innerHeight - viewportHeight : 0, iosInset);
}
